import * as _ from 'underscore';

import { EditConnectionAction } from '~src/connectivity/components/EditConnection/EditConnectionAction';
import {
  IEmbeddedPasswordUpdate
} from '~src/connectivity/resources/DataConnections';

import { CompositeSessionInfoSelectors } from '../../app/selectors/SessionInfoSelectors';
import { AppState } from '../../app/state/AppState';
import { Messages } from '../../codegen/Localize';
import { openReactAlertDialog } from '../../commonInfra/dialogs/ReactAlertDialog';
import { CommonDatasourceInfo } from '../../datasource/models/mappers/CommonDatasourceInfo';
import { IDataConnectionInfo, IDataConnectionInfoProperties } from '../../ts/models/IDataConnectionInfo';
import { IUserInfo } from '../../ts/models/IUserInfo';
import { IIdItemInfo, IOwnedItemInfo } from '../../ts/models/resourceInfoParts';
import { EditOAuthConnectionAction } from '../components/EditOAuthConnection/EditOAuthConnectionAction';

export let PasswordUnchanged: string = null;
export interface IDataConnectionEditModel {
  connectionType?: string;
  serverName?: string;
  serverPort?: string;
  username?: string;
  password?: IEmbeddedPasswordUpdate;
  usingRemoteQueryAgent?: boolean;
}

export type CredentialOption = 'none' | 'oauth' | 'password' | 'mixed';

export interface IOAuthConnectionEditModel {
  credentialOption: CredentialOption;
  credentialId?: string;
  credentialNoValueOptionText?: string; // this will be set if there is an extra null credential option
  usingManagedKeychain?: boolean; // this will be set if the OAuth Connection is using managed keychain as embedded credential.

  usernamePassword?: IDataConnectionEditModel;  // For Salesforce
}
export interface ITestConnectionResult {
  isPending: boolean;
  succeeded?: boolean;
  messages?: string[];
}

export type EditableConnection = IDataConnectionInfoProperties & IIdItemInfo;

export module ConnectionEditabilityHelper {

  export function isConnectionEditable(connection: EditableConnection) {
    return connection.connectionTypeEditable;
  }

  function getConnectionOwner(connection: IDataConnectionInfo): IUserInfo | null {
    const ownedItem: IOwnedItemInfo = connection.datasource ? connection.datasource : connection.flow;
    return ownedItem ? ownedItem.owner : null;
  }

  export function userCanEditConnection(connection: IDataConnectionInfo, state: AppState): boolean {
    const owner = getConnectionOwner(connection);
    const isAdminOrOwner = CompositeSessionInfoSelectors.currentUserIsAdminOrOwnerSelector(owner)(state);
    return isConnectionEditable(connection) && isAdminOrOwner;
  }

  export function userCanEditDatasource(datasource: CommonDatasourceInfo, state: AppState): boolean {
    // The frontend doesn't always know how many connections are in the data source, so we'll say
    // that if the user owns the data source, we'll allow them to attempt to edit. Once the user
    // selects the action, we call the backend to get all the associated connections, and then we know
    // for sure whether or not the data source is actually editable, and we can display a specific
    // error dialog if it's not.
    return CompositeSessionInfoSelectors.currentUserIsAdminOrOwnerSelector(datasource.owner)(state);
  }
}

export namespace EditConnectionDialog {

  export const SalesforceConnectionType = 'salesforce'; // Maybe move to ServerApi

  export function confirmEditConnections(connections: EditableConnection[]): Promise<any> {

    if (!haveSameConnectionType(connections)) {
      return openReactAlertDialog({
        title: Messages.editDataConnections_mixedConnectionTypesTitle(),
        message: Messages.editDataConnections_mixedConnectionTypesMessage()
      }).result;
    }

    if (!_.all(connections, ConnectionEditabilityHelper.isConnectionEditable)) {
      return openReactAlertDialog({
        title: Messages.editDataConnections_nonEditableTitle(),
        message: Messages.editDataConnections_nonEditableMessage()
      }).result;
    }

    if (connections[0].connectionOAuth) {
      return EditOAuthConnectionAction.confirmEditOAuthDatabaseConnections(connections);
    } else {
      return EditConnectionAction.confirmEditDatabaseConnections(connections);
    }
  }

  function haveSameConnectionType(connections: EditableConnection[]) {
    const firstConnectionType = connections[0].connectionType;
    return _.all(connections, c => c.connectionType === firstConnectionType);
  }
}
